import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { callWithSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only dialog to swap/replace a player on one slot of a scheduled
 * bracket match. The replacement must be a registered participant of the
 * tournament. Backend gates this with the Super Password.
 */
export default function BracketEditSlotDialog({
  open, onOpenChange, tournamentId, match, slot, players, onChanged,
}) {
  const [newPlayerId, setNewPlayerId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!match || !slot) return null;

  const currentPlayerId = slot === "player1" ? match.player1_id : match.player2_id;
  const opponentId = slot === "player1" ? match.player2_id : match.player1_id;
  const playerMap = Object.fromEntries((players || []).map((p) => [p.id, p]));
  const currentName = playerMap[currentPlayerId]?.name || match[`${slot}_name`] || "TBD";
  const otherSlotName = playerMap[opponentId]?.name
    || match[`${slot === "player1" ? "player2" : "player1"}_name`] || "TBD";

  // Eligible replacements = anyone except current slot & opponent.
  const eligible = (players || [])
    .filter((p) => p.id !== currentPlayerId && p.id !== opponentId)
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const handleConfirm = async () => {
    if (!newPlayerId) {
      toast.error("Pick a replacement player");
      return;
    }
    setSubmitting(true);
    try {
      const res = await callWithSuperPassword(
        ({ headers }) => axios.post(
          `${API}/tournaments/${tournamentId}/bracket-edit-slot`,
          { match_id: match.id, slot, new_player_id: newPlayerId },
          { headers, withCredentials: true },
        ),
        {
          title: "Confirm bracket edit",
          description: "Editing a bracket overrides the auto-seeded result. Enter the Super Password to continue.",
          actionLabel: "Update slot",
        },
      );
      if (!res) return; // admin cancelled the password prompt
      toast.success("Bracket slot updated");
      setNewPlayerId("");
      onOpenChange(false);
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to update slot";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card border-border"
        data-testid="bracket-edit-slot-dialog"
      >
        <DialogHeader>
          <DialogTitle>Edit bracket slot</DialogTitle>
          <DialogDescription>
            Replace the {slot === "player1" ? "left" : "right"} player in this match. Only allowed
            on scheduled knockout matches with both slots filled.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-border p-3 bg-muted/20 space-y-1.5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {(match.round || "match").replace(/_/g, " ")}
            </p>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span
                className={
                  slot === "player1"
                    ? "font-bold text-secondary truncate"
                    : "text-muted-foreground truncate"
                }
              >
                {currentName}
                {slot === "player1" && (
                  <span className="ml-2 text-[10px] text-secondary">(replacing)</span>
                )}
              </span>
              <span className="text-xs text-muted-foreground">vs</span>
              <span
                className={
                  slot === "player2"
                    ? "font-bold text-secondary truncate"
                    : "text-muted-foreground truncate"
                }
              >
                {otherSlotName}
                {slot === "player2" && (
                  <span className="ml-2 text-[10px] text-secondary">(replacing)</span>
                )}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Replace with</Label>
            <Select value={newPlayerId} onValueChange={setNewPlayerId}>
              <SelectTrigger
                className="bg-background border-border"
                data-testid="bracket-edit-slot-player-select"
              >
                <SelectValue placeholder="Select a player…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {eligible.length === 0 && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    No other players to choose from.
                  </div>
                )}
                {eligible.map((p) => (
                  <SelectItem
                    key={p.id}
                    value={p.id}
                    data-testid={`bracket-edit-slot-option-${p.id}`}
                  >
                    {p.name}
                    {p.tier ? ` · ${p.tier}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">
              Only players registered for this tournament are accepted.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="bracket-edit-slot-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={!newPlayerId || submitting}
              data-testid="bracket-edit-slot-confirm"
            >
              {submitting ? "Updating…" : "Update slot"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
